/**
 * The wire shapes `Rws.AiInsights.Host.Identity.IdentityQueueEndpoints` returns (phase 11, `AIA-81`):
 * pending identity-match candidates with their evidence, and people allocated a provider who hold no
 * seat with it. Gated on `accounts.administer`; revoking needs `entitlement.administer`.
 */
import { adminRequest, type AdminResult } from "./adminApi";

/** A person the candidate's email exactly matched — never a fuzzy suggestion. */
export interface NearMissPerson {
  personId: string;
  email: string;
  displayName: string;
}

/** One unattributed-consumption line attached to a candidate's vendor identity. */
export interface UnattributedConsumptionLine {
  periodStart: string;
  metric: string;
  value: number;
  unit: string;
  currency: string | null;
}

/** One pending identity-match candidate, with its evidence. */
export interface IdentityMatchCandidate {
  candidateId: string;
  vendorIdentityId: string;
  vendorId: string;
  externalId: string;
  email: string | null;
  matchReason: string;
  createdAt: string;
  nearMissPeople: NearMissPerson[];
  unattributedConsumption: UnattributedConsumptionLine[];
}

/** A person the portal allocates a provider to who holds no seat with that provider. */
export interface SeatlessAllocation {
  personId: string;
  displayName: string;
  email: string;
  vendorId: string;
  /** Whole-USD amount of the person's own allocation, or null when none of their access comes from one. */
  personalAmountUsd: number | null;
  /** Allowance groups currently giving the person this provider. */
  groupNames: string[];
  /** When their seat was first found missing; null when the portal never saw them hold one. */
  seatLastSeenGoneAt: string | null;
}

const candidatesPath = "/api/entitlement/identity-candidates";
const seatlessPath = "/api/entitlement/seatless-allocations";

/** Calls `GET /api/entitlement/identity-candidates`. */
export async function fetchIdentityCandidates(): Promise<AdminResult<IdentityMatchCandidate[]>> {
  return adminRequest<IdentityMatchCandidate[]>(candidatesPath, { method: "GET" }, "identity candidates");
}

/** Calls `GET /api/entitlement/seatless-allocations`. */
export async function fetchSeatlessAllocations(): Promise<AdminResult<SeatlessAllocation[]>> {
  return adminRequest<SeatlessAllocation[]>(seatlessPath, { method: "GET" }, "allocations without a seat");
}

/** Calls `POST /api/entitlement/seatless-allocations/revoke` — switches the person's own allocation for that provider off. */
export async function revokeSeatlessAllocation(personId: string, vendorId: string): Promise<AdminResult<null>> {
  return adminRequest<null>(`${seatlessPath}/revoke`, { method: "POST", body: JSON.stringify({ personId, vendorId }) }, "allocation revoke");
}

/** Calls `POST /api/entitlement/identity-candidates/{candidateId}/confirm`. */
export async function confirmIdentityCandidate(candidateId: string, personId: string): Promise<AdminResult<null>> {
  return adminRequest<null>(`${candidatesPath}/${encodeURIComponent(candidateId)}/confirm`, { method: "POST", body: JSON.stringify({ personId }) }, "candidate confirmation");
}

/** Calls `POST /api/entitlement/identity-candidates/{candidateId}/reject`. */
export async function rejectIdentityCandidate(candidateId: string): Promise<AdminResult<null>> {
  return adminRequest<null>(`${candidatesPath}/${encodeURIComponent(candidateId)}/reject`, { method: "POST" }, "candidate rejection");
}

/** Calls `POST /api/entitlement/identity-candidates/{candidateId}/mark-unattributable`. */
export async function markIdentityCandidateUnattributable(candidateId: string): Promise<AdminResult<null>> {
  return adminRequest<null>(`${candidatesPath}/${encodeURIComponent(candidateId)}/mark-unattributable`, { method: "POST" }, "mark unattributable");
}

/** The summary `POST /api/entitlement/provider-allocations/pre-create` returns. */
export interface PreCreateAllocationsResult {
  created: number;
  noUsageOrSeat: number;
  noAllowance: number;
  seatFeesExceedAllowance: number;
  refused: string[];
}

const preCreateAllocationsPath = "/api/entitlement/provider-allocations/pre-create";

/**
 * Calls `POST /api/entitlement/provider-allocations/pre-create` — gives every active person with no provider allocation a
 * first one, from their recent usage. Needs `entitlement.administer`.
 */
export async function preCreateProviderAllocations(): Promise<AdminResult<PreCreateAllocationsResult>> {
  return adminRequest<PreCreateAllocationsResult>(preCreateAllocationsPath, { method: "POST" }, "pre-create allocations");
}
